<template>
  <div class="v2-cart-actions">
    <div class="v2-cart-actions__left">
      <v2-button
        variant="outline"
        size="md"
        class="v2-cart-actions__back-btn"
        title="Back to Collection"
        @click="onClickBack"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </v2-button>
      <v2-button
        variant="secondary"
        size="md"
        class="v2-cart-actions__summary-btn"
        @click="onClickBuyingSummary"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line
            x1="3"
            y1="12"
            x2="21"
            y2="12"
          />
          <line
            x1="3"
            y1="6"
            x2="21"
            y2="6"
          />
          <line
            x1="3"
            y1="18"
            x2="21"
            y2="18"
          />
        </svg>
        <span>Buying Summary</span>
      </v2-button>
    </div>
    <div class="v2-cart-actions__right">
      <v2-button
        variant="outline"
        size="md"
        @click="onClickImportCart"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line
            x1="12"
            y1="15"
            x2="12"
            y2="3"
          />
        </svg>
        <span>Import</span>
      </v2-button>
      <v2-button
        variant="outline"
        size="md"
        @click="onClickExportMultipleCart"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line
            x1="12"
            y1="3"
            x2="12"
            y2="15"
          />
        </svg>
        <span>Export cart for all stores</span>
      </v2-button>
    </div>

    <!-- Buying Summary Modal -->
    <template v-if="cartList.length">
      <v2-buying-summary-modal
        :show="showBuyingSummaryModal"
        :cart-list="cartList"
        :cart-values-list="cartValuesList"
        :total-order-value="totalOrderValue"
        @close="onHideBuyingSummaryModal"
      />
      <v2-select-stores-modal
        :show="showSelectStoresModal"
        modal-title="Export cart of all stores"
        ok-btn-text="Export"
        :store-list="cartValuesList"
        :is-downloading="isExporting"
        @on-click-download="onClickExportCart"
        @on-change-stores="(selectedStores) => trackExportCartModal('change-stores', cartList, { exportType, selectedStoreIds: selectedStores.map(store => store.storeId) })"
        @on-close-modal="onCloseSelectStoresModal"
      />
    </template>
    <v2-cart-export-modal
      ref="cartExportModal"
      :is-multiple-cart="true"
      @on-close-export-cart-modal="trackExportCartModal('close', cartList)"
      @on-change-format="(newExportType) => trackExportCartModal('format-change', cartList, { newExportType })"
      @on-click-export-modal-download="(newExportType) => trackExportCartModal('download-click', cartList, { newExportType })"
      @on-cart-export-success="onCartExportSuccess"
      @on-cart-export-failed="onCartExportFailed"
      @open-export-multiple-carts-modal="openExportCartModal"
    />
  </div>
</template>

<script>
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import constants, { DOWNLOAD_TYPES } from '@/constants'
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import { EXPORT_CART_MULTIPLE } from '@/store/modules/shop.module'
import { UPDATE_CARTS } from '@/store/modules/checkout-v2.module'
import { SHOW_DOWNLOADS_NAV_INDICATOR } from '@/store/modules/notification.module'
import analytics from '@/@core/utils/analytics'
import V2Button from '@/components/v2/V2Button.vue'
import V2BuyingSummaryModal from './V2BuyingSummaryModal.vue'
import V2SelectStoresModal from './V2SelectStoresModal.vue'
import V2CartExportModal from './V2CartExportModal.vue'
import { trackExportCartModal } from '../../apps/checkout-v2/trackMultipleStoreCartHelpers'

const { CURRENCY_CONVERSIONS } = c

const CART_WITH_MULTIPLE_STORES = constants.TRACKS.ACTIONS.CART_WITH_MULTIPLE_STORES

export default {
  name: 'V2CartActions',
  components: {
    V2Button,
    V2BuyingSummaryModal,
    V2SelectStoresModal,
    V2CartExportModal,
  },
  props: {
    cartList: {
      type: Array,
      required: true,
    },
    cartValuesList: {
      type: Array,
      required: true,
    },
    totalOrderValue: {
      type: Object,
      default: () => ({
        totalCartsValue: 0,
        totalCartsUnits: 0,
        totalCartsRRP: 0,
        currency: CURRENCY_CONVERSIONS.USD.SYMBOL,
      }),
    },
  },
  data() {
    return {
      showBuyingSummaryModal: false,
      showSelectStoresModal: false,
      isExporting: false,
      exportType: '',
      selectedOptions: [],
      trackExportCartModal,
    }
  },
  methods: {
    onClickBack() {
      this.trackBackButton()
      this.$router.back()
    },
    trackBackButton() {
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_CLICKS_BACK_BUTTON, { carts: this.cartList })
    },
    onClickBuyingSummary() {
      this.showBuyingSummaryModal = true
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_CLICKED_BUYING_SUMMARY_BUTTON, { cartValuesList: this.cartValuesList })
    },
    onHideBuyingSummaryModal() {
      this.showBuyingSummaryModal = false
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_CLOSES_BUYING_SUMMARY_MODAL, { carts: this.cartList })
    },
    onClickImportCart() {
      this.$root.$emit('bv::show::modal', 'import_cart_modal')
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_CLICKED_IMPORT_CART_BUTTON)
    },
    onClickExportMultipleCart() {
      this.$refs.cartExportModal.show()
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_CLICKED_EXPORT_CART_BUTTON)
    },
    openExportCartModal({ exportType, selectedOptions }) {
      this.exportType = exportType
      this.selectedOptions = selectedOptions
      this.showSelectStoresModal = true
      trackExportCartModal('view-export-cart-of-all-modal', this.cartList, { exportType })
    },
    onCloseSelectStoresModal() {
      this.showSelectStoresModal = false
      trackExportCartModal('close-export-cart-of-all-modal', this.cartList, { exportType: this.exportType })
    },
    async onClickExportCart(selectedStores = []) {
      const selectedStoreIds = selectedStores.map(store => store.storeId)
      trackExportCartModal('export-all-download-click', this.cartList, { exportType: this.exportType, selectedStoreIds })
      try {
        await this.$store.dispatch(UPDATE_CARTS)
      } catch (err) {
        apiToastError(err)
        return
      }
      this.isExporting = true
      const downloadType = DOWNLOAD_TYPES.CART_EXPORT.VALUE
      const payload = {
        downloadType,
        fileType: this.exportType,
        respectiveIds: selectedStoreIds,
        optionalFields: this.selectedOptions,
      }

      this.$store.dispatch(EXPORT_CART_MULTIPLE, { payload }).then(res => {
        this.isExporting = false
        this.showSelectStoresModal = false
        apiToastSuccess(res.data.message)
        analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_EXPORT_CART_SUCCESSFULLY, { payload })
        this.$store.commit(SHOW_DOWNLOADS_NAV_INDICATOR)
      })
      .catch(err => {
        apiToastError(err)
        this.isExporting = false
        analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_EXPORT_CART_ERROR, err?.response)
      })
    },
    onCartExportSuccess() {
      this.$store.commit(SHOW_DOWNLOADS_NAV_INDICATOR)
    },
    onCartExportFailed() {
      // handled internally by V2CartExportModal via toast
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-cart-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;

  &__left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__back-btn {
    padding: 0.5rem;
    min-width: auto;
  }

  &__summary-btn {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }
}
</style>
